import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EcgsTableActionType,
  useEcgsTableContext,
} from "../../../context/EcgsTableContext";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../hooks/useAuthorizedAction";
import {
  EcgActionSeverity,
  IEcg,
  ReviewerActionsRequired,
  ReviewPriority,
} from "../../../models/ecg.interface";
import { mapEcgsToTableEcgs } from "../../../utils/ecgUtil";
import Table, { TableRef } from "../../Shared/Table/Table";
import styles from "./EcgsTable.module.css";
import { getEcgsTableColumns } from "./ecgsTableColumns";
import {
  generateSkeletonLoaderForTable,
  InfiniteScrollConfigType,
} from "../../../utils/infiniteScrollUtil";
import { Constants } from "../../../constants";
import "react-loading-skeleton/dist/skeleton.css";
import { Loader } from "@shltelemedicine/shl-ui-lib";

const { TABLE_ROWS_PER_SCROLL } = Constants;

export type EcgTableItem = {
  id: string;
  date: string;
  patientId: string;
  patientName: string;
  dateOfBirth: string;
  orgName?: string;
  entityName?: string;
  entityPhone?: string;
  entityEmail?: string;
  isUrgent?: boolean;
  unviewedEcg?: boolean;
  hasInterpretations?: boolean;
  hasFindings?: boolean;
  reviewPriority?: ReviewPriority;
  minutesRemaining?: number;
  isReviewed?: boolean;
  reference?: string;
  isApproved?: boolean;
  reviewType?: ReviewerActionsRequired;
  isSaved?: boolean;
  isArchived?: boolean;
  hasSymptoms?: boolean;
  isUnreadable?: boolean;
  state?: string;
  actionSeverity?: EcgActionSeverity;
};

export type EcgsTableRef = {
  curPage: number;
  nextDisabled: boolean;
  backDisabled: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  globalFilter: any;
  setGlobalFilter: any;
};

type EcgsTableProps = {
  itemsPerPage?: number;
  highlightText?: string;
  currentPage: number;
  pageAmount: number;
  setHasNextPage: (hasNextPage: boolean) => void;
};

const getPageIndexGroups = (pageAmount: number): number[][] => {
  const groups = [];
  for (let i = 0; i < 100; i += pageAmount) {
    const arr = [];
    for (let k = i; k < pageAmount + i; k++) {
      arr.push(k);
    }
    groups.push(arr);
  }
  return groups;
};

const EcgsTable = ({
  itemsPerPage,
  highlightText,
  currentPage,
  pageAmount,
  setHasNextPage,
}: EcgsTableProps) => {
  const {
    dispatch,
    state: { ecgs, selectedEcg, totalEcgs, tableRef },
  } = useEcgsTableContext();

  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const isApprover = useAuthorizedAction(AuthorizedActionsKeys.ApproveEcg);

  const isReviewer = useAuthorizedAction(AuthorizedActionsKeys.ReviewEcg);

  const isProfessional = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsProfessional
  );

  const refChangeHandler = useCallback(
    (ref: TableRef) => {
      if (ref !== null) {
        dispatch({
          type: EcgsTableActionType.SET_TABLE_REF,
          payload: ref,
        });
      }
    },
    [dispatch]
  );

  const ecgSelectedHandler = (ecg: EcgTableItem) => {
    const selectedEcg = ecgs.find((e) => e.id === ecg.id) ?? null;

    dispatch({
      type: EcgsTableActionType.SET_SELECTED_ECG,
      payload: selectedEcg,
    });
  };

  let totalPages = 0;
  if (itemsPerPage !== undefined && itemsPerPage !== null) {
    totalPages = Math.ceil(totalEcgs / itemsPerPage);
  }

  const tableColumns = getEcgsTableColumns({
    isCroOrg,
    isCroExternOrg,
    isReviewer,
    isApprover,
    isProfessional,
    highlightText,
  });

  const [currentPageEcgs, setCurrentPageEcgs] = useState<IEcg[]>([]);
  const [switchingPage, setSwitchingPage] = useState(false);
  const groups = useMemo(() => getPageIndexGroups(pageAmount!), [pageAmount]);
  useEffect(() => {
    //SET CURRENT PAGE ECGS
    if (!itemsPerPage || !ecgs.length || !groups.length) return;
    const currentPageIndex = groups
      .find((g) => g.includes(currentPage))
      ?.indexOf(currentPage);
    if (currentPageIndex === undefined) return;

    //SHOW ARTIFICIAL LOADER LIKE THE DATA IS BEING FETCHED
    pageAmount > 1 && setSwitchingPage(true);

    const startIndex = currentPageIndex * itemsPerPage;
    const newPageEcgs = ecgs.slice(startIndex, startIndex + itemsPerPage);
    setCurrentPageEcgs(newPageEcgs);

    //DISABLE NEXT BUTTON IF PAGE HAS LESS ITEMS THAN ITEMSPERPAGE AND THERES NO INDICATION OF THE SECOND PAGE
    const hasOneExtraEcg =
      ecgs.length.toString().substring(itemsPerPage.toString().length - 1) ===
      "1";
    if (newPageEcgs.length <= itemsPerPage && !hasOneExtraEcg)
      setHasNextPage(false);
    else setHasNextPage(true);

    //HIDE ARTIFICIAL LOADER, NO NEED FOR DELAY IF PAGEAMOUNT = 1
    const artificialScrollDelay = pageAmount > 1 ? 500 : 0;
    setTimeout(() => setSwitchingPage(false), artificialScrollDelay);
  }, [currentPage, ecgs, itemsPerPage, pageAmount, groups]);

  //SCROLL RELATED CONFIGS
  const [scrollConfig, setScrollConfig] = useState<
    Omit<InfiniteScrollConfigType<EcgTableItem>, "next">
  >({
    pageNumber: 1,
    hasMore: false,
    tableData: [],
    customLoader: generateSkeletonLoaderForTable(tableColumns.length + 1),
    dataLength: TABLE_ROWS_PER_SCROLL,
    scrollableTarget: "scrollableDivEcgsTable",
  });

  useEffect(() => {
    if (!currentPageEcgs.length) return;
    setScrollConfig({
      pageNumber: 1,
      hasMore: currentPageEcgs.length > 1 * TABLE_ROWS_PER_SCROLL,
      tableData: mapEcgsToTableEcgs(
        currentPageEcgs.slice(0, TABLE_ROWS_PER_SCROLL)
      ),
      customLoader: generateSkeletonLoaderForTable(tableColumns.length + 1),
      dataLength: TABLE_ROWS_PER_SCROLL,
      scrollableTarget: "scrollableDivEcgsTable",
    });
  }, [currentPageEcgs]);

  const getNextEcgBatch = async () => {
    setTimeout(() => {
      const nextPage = scrollConfig.pageNumber + 1;
      const newTableData = mapEcgsToTableEcgs(
        currentPageEcgs.slice(0, nextPage * TABLE_ROWS_PER_SCROLL)
      );
      const newScrollConfigs = {
        ...scrollConfig,
        pageNumber: nextPage,
        tableData: newTableData,
        hasMore:
          Math.min(currentPageEcgs.length, itemsPerPage!) >
          nextPage * TABLE_ROWS_PER_SCROLL,
        dataLength: newTableData.length,
      };
      setScrollConfig(newScrollConfigs);
    }, 500);
  };

  return switchingPage ? (
    <div className="pt-4">
      <Loader />
    </div>
  ) : (
    <Table<EcgTableItem>
      className={styles.table}
      tableData={scrollConfig.tableData}
      tableColumns={tableColumns}
      ref={refChangeHandler}
      itemsPerPage={itemsPerPage}
      currentPage={tableRef?.curPage}
      totalPages={totalPages}
      onRowSelect={ecgSelectedHandler}
      selectedRowId={selectedEcg?.id}
      customRowProps={(row) => ({
        className: `${!row?.original.unviewedEcg ? "viewed-ecg" : ""} ${
          row?.original.isArchived ? styles.archivedEcg : ""
        }`,
      })}
      serverSide
      scrollConfig={{ ...scrollConfig, next: getNextEcgBatch }}
    />
  );
};

export default EcgsTable;
